import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import moment from 'moment';
import { Button } from '@mui/material';


import { IShipment_DeletedSearchRequest, IShipment_DeletedSearchResult } from '../../../types/deleted';

import { Icons } from '../../icons';
import { AppConfig } from '../../../config/appConfig';
import Spinner from '../../spinner';
import { DeletedService } from '../../../services/deletedService';
import { ErrorProvider } from '../../../classes/errorProvider';

export interface IDeletedShipmentTableProps {
	data?: IShipment_DeletedSearchResult;
	bol?: string;
	customer?: string;
	vendor?: string;
	status?: string;
	sortField?: string;
	sortAscending?: boolean;
	isAwaitingResultSet?: boolean;
	userHasSearched?: boolean;
	update?: Function;
	onRestoreShipmentRequest(shipmentId: number, bol?: string): void;
}

const DeletedShipmentTable: React.FunctionComponent<IDeletedShipmentTableProps> = ({ data, bol, customer, vendor, status, sortField, sortAscending, isAwaitingResultSet, userHasSearched, update, onRestoreShipmentRequest }) => {
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [isRestoringShipment, setIsRestoringShipment] = useState(false);
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);	
	const [searchRequest, setSearchRequest] = useState<IShipment_DeletedSearchRequest>({
		pageIndex: 0,
		pageSize: pageSize,
		bol: bol,
		customer: customer,
		vendor: vendor,
		status: status,
		sortField: sortField,
		sortAscending: sortAscending,
		searchId: null
	});

	useEffect(() => {
		setPageIndex(searchRequest.pageIndex);
		setPageSize(searchRequest.pageSize);

		if (userHasSearched) {
			update(searchRequest);
		}
	}, [searchRequest]);

	useEffect(() => {
		setPageIndex(0);
	}, [bol, customer, vendor, status ])

	const handleHeaderClick = (field: string) => {
		if (isAwaitingResultSet || !userHasSearched) return;
		const request = { ...searchRequest };

		if (sortField == field) {
			request.sortAscending = !sortAscending;
		}
		else {
			request.sortAscending = true;
			request.sortField = field;
		}
		request.pageIndex = 0;

		setSearchRequest(request);
	}

	const handlePageBack = () => {
		if (pageIndex > 0) {
			const request = { ...searchRequest, ['pageIndex']: pageIndex - 1 }
			setSearchRequest(request);
		}
	}

	const handlePageForward = () => {
		if (pageIndex < (Math.floor(data?.totalMatchingRecords / pageSize))) {
			const request = { ...searchRequest, ['pageIndex']: pageIndex + 1 };
			setSearchRequest(request);
		}
	}

	const handleRestore = (shipmentId: number, bol?: string) => {
		onRestoreShipmentRequest?.(shipmentId, bol);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-bol">
					<p onClick={() => handleHeaderClick("bol")}>
						BOL#
						{" "}
						{(sortField == "bol" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "bol" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-customer">
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-vendor">
					<p onClick={() => handleHeaderClick("vendor")}>
						Vendor
						{" "}
						{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-pickup">
					<p onClick={() => handleHeaderClick("pickupdate")}>
						Shipped
						{" "}
						{(sortField == "pickupdate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "pickupdate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-status">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-deleted-date">
					<p onClick={() => handleHeaderClick("deleteddate")}>
						Deleted
						{" "}
						{(sortField == "deleteddate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "deleteddate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>					
				</div>
				<div className="col-deleted-by">
					<p onClick={() => handleHeaderClick("deletedby")}>
						Deleted By
						{" "}
						{(sortField == "deletedby" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "deletedby" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>					
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<div className="data">
				<Spinner dependency={isAwaitingResultSet} text="Loading Shipments...">
					{data?.records.length > 0 ?
						(
							data?.records.map((item, index) => (
								<div className={"row"} key={item.id}>
									<div className="col-bol">
										<p>
											{item.bolNumber + " "}
											{item.isLegacy &&
												<Icons.LegacyData fill="#A84825" height="12px"></Icons.LegacyData>
											}
										</p>
									</div>
									<div className="col-customer"><p>{item.customerName}</p></div>
									<div className="col-vendor"><p>{item.vendorName}</p></div>
									<div className="col-pickup"><p>{item.pickupDate ? moment(item.pickupDate).format("MM/DD/yyyy") : ""}</p></div>
									<div className="col-status"><p>{item.status}</p></div>
									<div className="col-deleted-date"><p>{item.deletedDate ? moment(item.deletedDate).format("MM/DD/yyyy") : ""}</p></div>									
									<div className="col-deleted-by"><p>{item.deletedBy}</p></div>									
									<div className="col-controls">
										<Button onClick={() => { handleRestore(item.id, item.bolNumber) }} color="primary">
											<Icons.LegacyData fill="#272f50" height="16px"></Icons.LegacyData>
											Restore
										</Button> 
									</div>
								</div>
							))
						) :
						(
							<div className="no-results">{userHasSearched ? '0 results' : 'Please enter search criteria or a BOL, Invoice, or Manifest #'}</div>
						)
					}
				</Spinner>
			</div>
			<div className="pager">
				{data?.records.length > 0 &&
					<>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={isAwaitingResultSet || pageIndex <= 0} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${(pageIndex * pageSize) + 1} to ${Math.min((pageIndex * pageSize) + pageSize, data?.totalMatchingRecords)} of ${data?.totalMatchingRecords}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={isAwaitingResultSet || ((pageIndex + 1) * pageSize) >= data?.totalMatchingRecords} onClick={handlePageForward}>Next</Button></div>
					</>
				}
			</div>
		</div>
	)
}

export default DeletedShipmentTable;